import {
  ApplicationConfig,
  computed,
  inject,
  isDevMode,
  LOCALE_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeEnGb from '@angular/common/locales/en-GB';
import localeNb from '@angular/common/locales/nb';
import localeNn from '@angular/common/locales/nn';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { TranslateService, provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { LocalStorageTokenStore, TOKEN_STORE, authInterceptor, provideAuth } from '@nabotavle/auth';
import { provideNbIcons, provideNbUiText, provideTheme, type NbUiText } from '@nabotavle/ui/core';
import { I18N_SUFFIX, LocaleService, resolveInitialLocale, toAngularLocale } from '@nabotavle/util';
import { Bell, CalendarDays, ChevronLeft, House, Megaphone, Plus, User, Users } from 'lucide';

import { routes } from './app.routes';

// Norwegian dates/numbers everywhere (DatePipe etc.) — without this Angular
// defaults to en-US ("Thursday 18. June" instead of "torsdag 18. juni").
// All three shipped locales, so DatePipe/number formatting follows the chosen language (LOCALE_ID is
// resolved once at bootstrap from the persisted choice; a switch applies fully from the next load).
registerLocaleData(localeNb);
registerLocaleData(localeNn);
registerLocaleData(localeEnGb);

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useValue: toAngularLocale(resolveInitialLocale()) },
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptors([authInterceptor])),
    // i18n (V1.12) — runtime locale switching via ngx-translate. JSON served from /i18n/<lang>.json
    // (public/i18n). Initial language by precedence: persisted > browser > nb; nb is the fallback.
    provideTranslateService({
      loader: provideTranslateHttpLoader({ prefix: '/i18n/', suffix: I18N_SUFFIX }),
      fallbackLang: 'nb',
      lang: resolveInitialLocale(),
    }),
    provideAppInitializer(() => inject(LocaleService).init()),
    provideAuth({ apiBase: '/api' }),
    // The session token lives in localStorage (the native SwiftUI app has its own Keychain store).
    { provide: TOKEN_STORE, useClass: LocalStorageTokenStore },
    // Design-system copy (close buttons, search placeholders, palette chrome, trend words) follows the
    // chosen language, so a component's built-in label never falls back to Norwegian in nn/en.
    provideNbUiText(() => {
      const t = inject(TranslateService);
      return computed(() => {
        t.currentLang();
        const ui = t.instant('ui') as Partial<NbUiText> | string;
        return typeof ui === 'object' ? ui : {};
      });
    }),
    provideTheme({ scheme: 'system' }),
    provideNbIcons({ House, Bell, Plus, Megaphone, ChevronLeft, Users, CalendarDays, User }),
    // PWA service worker (production builds only).
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
